import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import Layout and Pages
import Layout from './components/Layout';
import Login from './pages/Login/Login';
import ProtectedRoute from './components/ProtectedRoute'; // Import once

// Import existing pages
import Dashboard from './pages/Dashboard/Dashboard';
import Products from './pages/Products/Products';
import ProductForm from './pages/Products/ProductForm';
import Customers from './pages/Customers/Customers';
import EditCustomer from './pages/Customers/EditCustomer';
import Invoices from './pages/Invoices/Invoices';
import InvoiceForm from './pages/Invoices/InvoiceForm';
import Sales from './pages/Sales/Sales';
import SalesForm from './pages/Sales/SalesForm';
import Rentals from './pages/Rentals/Rentals';
import ReportsPage from './pages/Reports/ReportsPage';
import Settings from './pages/Settings/Settings';
import Quotations from './pages/Quotations/Quotations';
import QuotationForm from './pages/Quotations/QuotationForm';
import Users from './pages/Users/Users';
import UserForm from './pages/Users/UserForm';
import ViewSale from './pages/Sales/ViewSale';
import POS from './pages/POS/POS';
import POSSales from './pages/POS/POSSales';
import RepairList from './pages/Repairs/RepairList';
import RepairForm from './pages/Repairs/RepairForm';
import RepairView from './pages/Repairs/RepairView';

// ==================== IMPORT PRODUCT ISSUES PAGE ====================
import ProductIssues from './pages/ProductIssues/ProductIssues';

// Credit Management Pages
import CreditDashboard from './pages/Credit/CreditDashboard';
import CreditCustomers from './pages/Credit/CreditCustomers';
import OverdueAccounts from './pages/Credit/OverdueAccounts';
import PaymentCollection from './pages/Credit/PaymentCollection';

// School Projects Pages
import SchoolProjectDashboard from './pages/SchoolProjects/SchoolProjectDashboard';
import SchoolsManagement from './pages/SchoolProjects/SchoolsManagement';
import InventoryManagement from './pages/SchoolProjects/InventoryManagement';
import ProjectsList from './pages/SchoolProjects/ProjectsList';
import SchoolProjectDetails from './pages/SchoolProjects/ProjectDetails';
import SchoolProjectForm from './pages/SchoolProjects/ProjectForm';
import PurchasesManagement from './pages/SchoolProjects/PurchasesManagement';
import TechniciansManagement from './pages/SchoolProjects/TechniciansManagement';
import TechnicianParts from './pages/SchoolProjects/TechnicianParts/TechnicianParts';
import IssueParts from './pages/SchoolProjects/TechnicianParts/IssueParts';
import ReturnParts from './pages/SchoolProjects/TechnicianParts/ReturnParts';
import AssignmentsList from './pages/SchoolProjects/TechnicianParts/AssignmentsList';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

// Public Route Component (for login page)
const PublicRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('erp_auth_token');
  
  if (isAuthenticated) {
    // If already logged in, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Public Route - Login Page */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />

          {/* Default route redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Protected Routes - All wrapped with Layout */}

          {/* Dashboard - Accessible to all authenticated users */}
          <Route path="/dashboard" element={
            <ProtectedRoute requiredRole="all">
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />

          {/* POS */}
          <Route path="/pos" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <POS />
              </Layout>
            </ProtectedRoute>
          } />

          {/* POS Sales History */}
          <Route path="/pos/sales" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <POSSales />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Products - Admin only */}
          <Route path="/products" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <Products />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Product Form */}
          <Route path="/products/new" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <ProductForm />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/products/edit/:id" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <ProductForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* ==================== PRODUCT ISSUES ROUTE ==================== */}
          <Route path="/product-issues" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <ProductIssues />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Customers - Admin only */}
          <Route path="/customers" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <Customers />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Edit Customer */}
          <Route path="/customers/edit/:id" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <EditCustomer />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Invoices */}
          <Route path="/invoices" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <Invoices />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/new" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <InvoiceForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/edit/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <InvoiceForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <InvoiceForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Sales - Admin & Staff */}
          <Route path="/sales" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <Sales />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/sales/new" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SalesForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/sales/edit/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SalesForm />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/sales/view/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <ViewSale />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Quotations - Admin & Staff */}
          <Route path="/quotations" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <Quotations />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/new" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <QuotationForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/quotations/edit/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <QuotationForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Users - Admin only */}
          <Route path="/users" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <Users />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/users/new" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <UserForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/users/edit/:id" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <UserForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Other Modules */}
          <Route path="/rentals" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <Rentals />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <ReportsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Credit Management Routes */}
          <Route path="/credit/dashboard" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <CreditDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credit/customers" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <CreditCustomers />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credit/overdue" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <OverdueAccounts />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/credit/payments" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <PaymentCollection />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Repair Management Routes */}
          <Route path="/repairs" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <RepairList />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/new" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <RepairForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/edit/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <RepairForm />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/repairs/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <RepairView />
              </Layout>
            </ProtectedRoute>
          } />

          {/* ==================== SCHOOL PROJECTS ROUTES ==================== */}
          
          {/* School Projects Dashboard */}
          <Route path="/school-projects" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SchoolProjectDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/school-projects/dashboard" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SchoolProjectDashboard />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Projects */}
          <Route path="/school-projects/projects" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <ProjectsList />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Create New Project */}
          <Route path="/school-projects/projects/new" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SchoolProjectForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Edit Project */}
          <Route path="/school-projects/projects/edit/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SchoolProjectForm />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Project Details */}
          <Route path="/school-projects/projects/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <SchoolProjectDetails />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Schools Management */}
          <Route path="/school-projects/schools" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <SchoolsManagement />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Inventory Management */}
          <Route path="/school-projects/inventory" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <InventoryManagement />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Purchases Management */}
          <Route path="/school-projects/purchases" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <PurchasesManagement />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Technicians Management */}
          <Route path="/school-projects/technicians" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <TechniciansManagement />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Reports Management */}
          <Route path="/school-projects/reports" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <div style={{ padding: '20px' }}>
                  <h1>School Projects Reports</h1>
                  <p>Reports module will be implemented soon.</p>
                </div>
              </Layout>
            </ProtectedRoute>
          } />

          {/* Technician Parts Management */}
          <Route path="/school-projects/technician-parts" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <TechnicianParts />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/school-projects/technician-parts/issue" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <IssueParts />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/school-projects/technician-parts/return" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <ReturnParts />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/school-projects/technician-parts/assignments" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <AssignmentsList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/school-projects/technician-parts/assignments/:id" element={
            <ProtectedRoute requiredRole="staff">
              <Layout>
                <AssignmentsList />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Settings Management */}
          <Route path="/school-projects/settings" element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <div style={{ padding: '20px' }}>
                  <h1>School Projects Settings</h1>
                  <p>Settings module will be implemented soon.</p>
                </div>
              </Layout>
            </ProtectedRoute>
          } />

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;